import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  QrCode as QrCodeIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';

// Services
import { getWarehouses } from '../services/warehouseService';
import {
  getPartsForBundlePrinting,
  createBundleLabels
} from '../services/technicianInventoryService';

const BundlePrintDialog = ({ open, onClose }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [partsData, setPartsData] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('select'); // 'select', 'preview', 'labels'

  // Load warehouses when dialog opens
  useEffect(() => {
    if (open) {
      loadWarehouses();
    }
  }, [open]);

  const loadWarehouses = async () => {
    try {
      const warehouseData = await getWarehouses();
      setWarehouses(Array.isArray(warehouseData) ? warehouseData : []);
    } catch (err) {
      console.error('Error loading warehouses:', err);
      setError('Failed to load warehouses');
    }
  };

  const handleWarehouseSelect = async (warehouseId) => {
    setSelectedWarehouse(warehouseId);
    if (warehouseId) {
      await loadPartsData(warehouseId);
    }
  };

  const loadPartsData = async (warehouseId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPartsForBundlePrinting(warehouseId);
      setPartsData(data);
      setStep('preview');
    } catch (err) {
      console.error('Error loading parts data:', err);
      setError(err.message || 'Failed to load parts data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLabels = async () => {
    try {
      setLoading(true);
      setError(null);
      const labelData = await createBundleLabels(selectedWarehouse);
      setBundles(labelData.bundles);
      setStep('labels');
    } catch (err) {
      console.error('Error creating labels:', err);
      setError(err.message || 'Failed to create labels');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintLabels = async () => {
    try {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');

      // Show loading message in print window
      printWindow.document.write(`
        <html>
          <head><title>Generating Labels...</title></head>
          <body style="font-family: Arial, sans-serif; text-align: center; padding: 50px;">
            <h2>Generating QR codes...</h2>
            <p>Please wait while we generate your labels.</p>
          </body>
        </html>
      `);

      // Generate print content with QR codes
      const printContent = await generatePrintContent();

      // Clear the loading message and write the actual content
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Technician Bundle Labels</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
              .label {
                width: 4in;
                height: 6in;
                border: 2px solid #000;
                margin: 0.25in;
                padding: 0.25in;
                page-break-after: always;
                page-break-inside: avoid;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                transform: rotate(180deg);
                transform-origin: center;
                box-sizing: border-box;
                overflow: hidden;
              }
              .qr-code-container {
                text-align: center;
                margin: 10px 0;
                padding: 10px;
                border: 1px solid #ccc;
                background: #f9f9f9;
              }
              .qr-text {
                font-size: 12px;
                margin-top: 5px;
                color: #666;
              }
              .qr-reference {
                text-align: center;
                margin: 10px 0;
                padding: 10px;
                border: 1px solid #ccc;
                background: #f0f0f0;
              }
              .bundle-info {
                font-size: 11px;
                color: #666;
                margin-top: 5px;
              }
              .page-info {
                font-size: 12px;
                font-weight: bold;
                text-align: center;
                color: #333;
                margin-top: 5px;
              }
              .title { font-size: 18px; font-weight: bold; text-align: center; margin-bottom: 10px; }
              .parts-list { font-size: 12px; flex-grow: 1; }
              .part-item { margin: 5px 0; padding: 5px; border-bottom: 1px solid #ccc; }
              .part-item.more-parts { background: #f0f8ff; border: 1px solid #4a90e2; border-radius: 4px; font-style: italic; text-align: center; }
              .technician { font-size: 14px; font-weight: bold; color: #666; text-align: center; }
              .instructions { font-size: 10px; color: #666; margin-top: 10px; }
              @media print {
                body { margin: 0; padding: 0; }
                .label {
                  margin: 0;
                  transform: rotate(180deg);
                  transform-origin: center;
                }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.print();

    } catch (error) {
      console.error('Error generating print labels:', error);
      alert('Error generating labels. Please try again.');
    }
  };

  const generateQRCodeSVG = async (qrCode, size = 160) => {
    const baseUrl = window.location.origin;

    try {
      // Use the /api/qr/inventory endpoint which handles short link creation internally
      const response = await fetch(`${baseUrl}/api/qr/inventory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          qr_code: qrCode,
          status: 'on_truck',
          format: 'dataurl',
          width: size,
          useShortLink: true, // This will create short links via l.emshvac.co
          shortLinkTitle: `Bundle QR: ${qrCode.substring(0, 50)}`
        })
      });

      if (!response.ok) {
        throw new Error(`QR generation failed: ${response.status}`);
      }

      const data = await response.json();
      return `<img src="${data.dataURL}" alt="QR Code" style="width: ${size}px; height: ${size}px; border: 1px solid #000;">`;

    } catch (error) {
      console.error('Error generating QR code:', error);
      // Fallback to 3rd party service with short link
      const scanUrl = `${baseUrl}/qr-scan?qr=${encodeURIComponent(qrCode)}&status=on_truck`;

      try {
        // Try to create a short link first
        const shortLinkResponse = await fetch('https://l.emshvac.co/api/links', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'ApiKey ShortLinkAPIKey2025!ForJWTOverride'
          },
          body: JSON.stringify({
            original_url: scanUrl,
            title: `Bundle QR: ${qrCode.substring(0, 50)}`
          })
        });

        if (shortLinkResponse.ok) {
          const shortLinkData = await shortLinkResponse.json();
          if (shortLinkData.success) {
            const shortUrl = `https://l.emshvac.co/${shortLinkData.data.short_code}`;
            console.log(`Created short link for fallback QR: ${scanUrl} -> ${shortUrl}`);
            const fallbackUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(shortUrl)}`;
            return `<img src="${fallbackUrl}" alt="QR Code" style="width: ${size}px; height: ${size}px; border: 1px solid #000;">`;
          }
        }
      } catch (shortLinkError) {
        console.error('Short link creation failed in fallback:', shortLinkError);
      }

      // Final fallback with long URL if short link fails
      const fallbackUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(scanUrl)}`;
      return `<img src="${fallbackUrl}" alt="QR Code" style="width: ${size}px; height: ${size}px; border: 1px solid #000;">`;
    }
  };

  const generatePrintContent = async () => {
    const bundleContents = await Promise.all(bundles.map(async (bundle) => {
      const qrCodeSVG = await generateQRCodeSVG(bundle.qr_code, 280);
      const isBundle = bundle.type === 'bundle';

      // With short links, bundles can contain unlimited parts without QR code size constraints
      return `
        <div class="label">
          <div>
            <div class="title">${bundle.label_data.title}</div>
            <div class="technician">For: ${bundle.technician.name}</div>
          </div>

          <div class="qr-code-container">
            ${qrCodeSVG}
            <div class="qr-text">Scan to update status</div>
          </div>

          <div class="parts-list">
            ${isBundle ?
              bundle.label_data.parts_list.map(part =>
                `<div class="part-item">
                  <strong>${part.part_number}</strong><br>
                  ${part.description}<br>
                  Qty: ${part.quantity}
                </div>`
              ).join('') +
              (bundle.label_data.is_multi_label && !bundle.label_data.is_last_label ?
                `<div class="part-item more-parts">
                  <strong>Continued on next label...</strong><br>
                  Total bundle: ${bundle.label_data.parts_count} items (${bundle.label_data.unique_parts_count} unique)
                </div>` :
                bundle.label_data.is_multi_label && bundle.label_data.is_last_label ?
                `<div class="part-item more-parts">
                  <strong>End of bundle</strong><br>
                  Total: ${bundle.label_data.parts_count} items (${bundle.label_data.unique_parts_count} unique)
                </div>` : '') :
              `<div class="part-item">
                <strong>${bundle.label_data.title}</strong><br>
                ${bundle.label_data.description}<br>
                Qty: ${bundle.label_data.quantity}
              </div>`
            }
          </div>

          <div class="instructions">
            <strong>Instructions:</strong><br>
            Scan QR code with phone to update status to "On Truck"<br>
            ${bundle.is_persistent ?
              '<strong>Note:</strong> This QR code can be used multiple times' :
              '<strong>Note:</strong> This QR code is for one-time use only'
            }
          </div>
        </div>
      `;
    }));

    return bundleContents.join('');
  };

  const handleClose = () => {
    setStep('select');
    setSelectedWarehouse('');
    setPartsData(null);
    setBundles([]);
    setError(null);
    onClose();
  };

  const getTotalParts = () => {
    if (!partsData) return 0;
    return partsData.technicians.reduce((total, tech) => total + tech.parts.length, 0);
  };

  const getBundleStats = () => {
    if (!bundles.length) return { bundleLabels: 0, individualLabels: 0, totalPages: 0 };

    let bundleLabels = 0;
    let individualLabels = 0;

    bundles.forEach(bundle => {
      if (bundle.type === 'bundle') {
        bundleLabels++;
      } else {
        individualLabels++;
      }
    });

    // Each bundle/individual is one page
    const totalPages = bundles.length;

    return { bundleLabels, individualLabels, totalPages };
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon />
          Print Technician Bundle Labels
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 'select' && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Select a warehouse to print bundle labels for parts assigned to technicians.
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Select Warehouse</InputLabel>
              <Select
                value={selectedWarehouse}
                onChange={(e) => handleWarehouseSelect(e.target.value)}
                label="Select Warehouse"
                disabled={loading}
              >
                {warehouses.map(warehouse => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name} - {warehouse.location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        )}

        {step === 'preview' && partsData && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview: {partsData.warehouse.name}
            </Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Total Parts:</strong> {getTotalParts()} parts assigned to {partsData.technicians.length} technician(s)
              </Typography>
            </Box>

            {partsData.technicians.map(tech => (
              <Paper key={tech.technician.id} sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6">{tech.technician.name}</Typography>
                  <Chip
                    label={tech.technician.type}
                    size="small"
                    color={tech.technician.type === 'technician' ? 'primary' : 'secondary'}
                  />
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Can Bundle</TableCell>
                        <TableCell>Special Order</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tech.parts.map(part => (
                        <TableRow key={part.id}>
                          <TableCell>{part.part.internal_part_number}</TableCell>
                          <TableCell>{part.part.description}</TableCell>
                          <TableCell>{part.quantity}</TableCell>
                          <TableCell>
                            <Chip
                              label={part.part.can_bundle ? 'Yes' : 'No'}
                              size="small"
                              color={part.part.can_bundle ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={part.part.is_special_order ? 'Yes' : 'No'}
                              size="small"
                              color={part.part.is_special_order ? 'warning' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Box>
        )}

        {step === 'labels' && bundles.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Labels Ready for Printing
            </Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Labels Created:</strong> {getBundleStats().bundleLabels} bundle labels, {getBundleStats().individualLabels} individual labels
              </Typography>
              <Typography variant="body2">
                <strong>Total Pages to Print:</strong> {getBundleStats().totalPages} pages
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2 }}>
              The following labels will be printed (4x6 format, automatically rotated for correct printer orientation):
            </Typography>

            {bundles.map((bundle, index) => (
              <Paper key={index} sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <QrCodeIcon color="primary" />
                  <Typography variant="subtitle1">
                    {bundle.type === 'bundle' ? 'Bundle Label' : 'Individual Label'}
                  </Typography>
                  <Chip
                    label={bundle.is_persistent ? 'Persistent QR' : 'One-time QR'}
                    size="small"
                    color={bundle.is_persistent ? 'warning' : 'default'}
                  />
                </Box>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>For:</strong> {bundle.technician.name}
                </Typography>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>QR Code:</strong> {bundle.qr_code.length > 50 ? `${bundle.qr_code.substring(0, 50)}...` : bundle.qr_code}
                </Typography>

                <Typography variant="body2">
                  <strong>Parts:</strong> {bundle.parts.length} item(s)
                  {bundle.type === 'bundle' && bundle.label_data.unique_parts_count !== bundle.parts.length && (
                    <span> ({bundle.label_data.unique_parts_count} unique)</span>
                  )}
                </Typography>

                {bundle.type === 'bundle' && bundle.parts.length > 20 && (
                  <Typography variant="body2" color="info.main">
                    <strong>Note:</strong> Large bundle ({bundle.parts.length} items) - QR code uses short link for optimal scanning
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {step === 'labels' ? 'Close' : 'Cancel'}
        </Button>

        {step === 'select' && (
          <Button
            disabled={!selectedWarehouse || loading}
            variant="contained"
            onClick={() => handleWarehouseSelect(selectedWarehouse)}
          >
            {loading ? 'Loading...' : 'Load Parts'}
          </Button>
        )}

        {step === 'preview' && (
          <Button
            variant="contained"
            onClick={handleCreateLabels}
            disabled={loading || !partsData?.technicians.length}
          >
            {loading ? 'Creating...' : 'Create Labels'}
          </Button>
        )}

        {step === 'labels' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrintLabels}
          >
            Print Labels (Auto-Rotated)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BundlePrintDialog;
